import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import styles from './LogicalCanvas.module.css'

const UNSCALED_ROUTES = new Set([
  '/',
  '/youtube',
  '/users/auth/oculus/callback',
  '/users/auth/youtube/callback',
  '/404',
  '/_error',
])
const SCALE_ONE_FACTOR = 2 / 3
const LOGICAL_CANVAS_HEIGHT = 1425

const LogicalCanvasContext = createContext(null)

export function useLogicalCanvasPortal() {
  const context = useContext(LogicalCanvasContext)

  return context || {
    canvasHeight: typeof window !== 'undefined' ? window.innerHeight : LOGICAL_CANVAS_HEIGHT,
    portalTarget: null,
    scale: 1,
    getLogicalCanvasPosition: ({ top, left }) => ({
      top: top + (typeof window !== 'undefined' ? window.scrollY : 0),
      left: left + (typeof window !== 'undefined' ? window.scrollX : 0),
    }),
  }
}

export default function LogicalCanvas({ children, pathname }) {
  const canvasRef = useRef(null)
  const [portalTarget, setPortalTarget] = useState(null)
  const isScaleOneRoute = !UNSCALED_ROUTES.has(pathname)

  const getLogicalCanvasPosition = useCallback(({ top, left }) => {
    const canvasRect = canvasRef.current?.getBoundingClientRect()

    if (!canvasRect) return { top, left }

    return {
      top: (top - canvasRect.top) / SCALE_ONE_FACTOR,
      left: (left - canvasRect.left) / SCALE_ONE_FACTOR,
    }
  }, [])

  const context = useMemo(() => ({
    canvasHeight: LOGICAL_CANVAS_HEIGHT,
    getLogicalCanvasPosition,
    portalTarget,
    scale: SCALE_ONE_FACTOR,
  }), [getLogicalCanvasPosition, portalTarget])

  if (!isScaleOneRoute) return children

  return (
    <LogicalCanvasContext.Provider value={context}>
      <div className={styles.canvas} data-logical-canvas="scale-one" ref={canvasRef}>
        <div className={styles.content}>{children}</div>
        <div
          className={styles.overlay}
          data-logical-canvas-overlay="true"
          ref={setPortalTarget}
        />
      </div>
    </LogicalCanvasContext.Provider>
  )
}
